import { useCallback, useEffect, useRef, useState } from 'react'
import { useMutation, useQuery, useQueryClient, type QueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'

import { deleteDocument, fetchBanking, fetchDocuments, uploadDocument, UploadCancelledError } from '@/api/riskPortal.api'
import { ACCEPTED_FILE_TYPES } from '@/components/documents/FileDropZone'
import {
  DocumentRequirementCard,
  type PendingUpload,
} from '@/components/documents/DocumentRequirementCard'
import { DocumentRequirementCardSkeleton } from '@/components/dashboard/DocumentRequirementCardSkeleton'
import { DashboardSkeleton } from '@/components/dashboard/DashboardSkeleton'
import {
  DashboardSectionHeading,
  DashboardSidebarHeader,
  DashboardWelcomeHero,
} from '@/components/dashboard/dashboardLayout'
import { ProgressSummaryCard } from '@/components/dashboard/ProgressSummaryCard'
import { VerificationCompleteDialog } from '@/components/dashboard/VerificationCompleteDialog'
import { SecurityCard } from '@/components/dashboard/SecurityCard'
import { BANKING_QUERY_KEY, BankVerificationCard } from '@/components/dashboard/BankVerificationCard'
import { ErrorState } from '@/components/ErrorState'
import { useAuth } from '@/contexts/AuthContext'
import { ApiError } from '@/api/baseApi'
import { formatPrivateDisplayName } from '@/utils/format'
import {
  hasSatisfyingDocument,
  type RiskPortalDocumentsResponse,
  type RiskPortalExternalDocument,
} from '@/types/riskPortal.types'

const MAX_FILE_SIZE_BYTES = 25 * 1024 * 1024
const ALLOWED_EXTENSIONS = new Set(ACCEPTED_FILE_TYPES.split(',').map((ext) => ext.trim().toLowerCase()))

const DOCUMENTS_QUERY_KEY = ['portal-documents'] as const

function getExtension(fileName: string): string {
  const dot = fileName.lastIndexOf('.')
  return dot === -1 ? '' : fileName.slice(dot).toLowerCase()
}

function messageFromError(error: unknown, fallback: string): string {
  if (error instanceof ApiError) {
    const data = error.data as { detail?: string } | null
    if (data?.detail) return data.detail
  }
  return fallback
}

function addDocumentToRequirementsCache(
  queryClient: QueryClient,
  requirementKey: string,
  document: RiskPortalExternalDocument,
) {
  queryClient.setQueryData<RiskPortalDocumentsResponse>(DOCUMENTS_QUERY_KEY, (existing) => {
    if (!existing) return existing

    const requirements = existing.requirements.map((requirement) => {
      if (requirement.key !== requirementKey) return requirement
      if (requirement.documents.some((doc) => doc.id === document.id)) {
        return requirement
      }

      return {
        ...requirement,
        documents: [...requirement.documents, document],
      }
    })

    const completedRequired = requirements.filter(
      (requirement) => !requirement.optional && hasSatisfyingDocument(requirement.documents),
    ).length

    return {
      ...existing,
      requirements,
      completedRequired,
    }
  })
}


export function Dashboard() {
  const { riskUser } = useAuth()
  const queryClient = useQueryClient()

  const { data, isLoading, isError, refetch } = useQuery({
    queryKey: DOCUMENTS_QUERY_KEY,
    queryFn: fetchDocuments,
    enabled: !!riskUser,
  })

  // Shares the cache entry with BankVerificationCard (same key), so only one request goes out.
  const { data: banking, isLoading: isBankingLoading } = useQuery({
    queryKey: BANKING_QUERY_KEY,
    queryFn: fetchBanking,
    enabled: !!riskUser,
  })

  const [pendingUploads, setPendingUploads] = useState<PendingUpload[]>([])
  const [showCompleteDialog, setShowCompleteDialog] = useState(false)
  const wasCompleteRef = useRef<boolean | null>(null)
  const [removingIds, setRemovingIds] = useState<Set<string>>(new Set())
  // Keep the raw File for each in-flight/failed upload so a retry can resend it.
  const pendingFilesRef = useRef<Map<string, File>>(new Map())
  // Abort controller per in-flight upload so a cancel button can stop the request mid-flight.
  const pendingControllersRef = useRef<Map<string, AbortController>>(new Map())

  const runUpload = useCallback(
    (pendingId: string, requirementKey: string, file: File) => {
      const controller = new AbortController()
      pendingControllersRef.current.set(pendingId, controller)
      uploadDocument(requirementKey, file, undefined, controller.signal)
        .then((document) => {
          addDocumentToRequirementsCache(queryClient, requirementKey, document)
          setPendingUploads((current) => current.filter((p) => p.id !== pendingId))
          pendingFilesRef.current.delete(pendingId)
          pendingControllersRef.current.delete(pendingId)
          toast.success(`${file.name} uploaded`)
        })
        .catch((error: unknown) => {
          pendingControllersRef.current.delete(pendingId)
          // A user-initiated cancel already removed the row in handleCancel — don't flag it as an error.
          if (error instanceof UploadCancelledError) {
            return
          }
          const message = messageFromError(error, 'Upload failed. Please try again.')
          setPendingUploads((current) =>
            current.map((p) =>
              p.id === pendingId ? { ...p, error: message, status: undefined } : p,
            ),
          )
        })
    },
    [queryClient],
  )

  const handleUploadAnimationComplete = useCallback((pendingId: string) => {
    setPendingUploads((current) => current.filter((p) => p.id !== pendingId))
    pendingFilesRef.current.delete(pendingId)
  }, [])

  const handleFilesSelected = useCallback(
    (requirementKey: string, files: File[]) => {
      for (const file of files) {
        if (file.size === 0 || file.size > MAX_FILE_SIZE_BYTES || !ALLOWED_EXTENSIONS.has(getExtension(file.name))) {
          toast.error(
            `${file.name} can't be uploaded. Use a PDF, image, spreadsheet, or Word document up to 25MB.`,
          )
          continue
        }

        const pendingId = crypto.randomUUID()
        pendingFilesRef.current.set(pendingId, file)
        setPendingUploads((current) => [
          ...current,
          { id: pendingId, requirementKey, fileName: file.name, progress: 0, status: 'uploading' },
        ])
        runUpload(pendingId, requirementKey, file)
      }
    },
    [runUpload],
  )

  const handleRetry = useCallback(
    (pendingId: string) => {
      const file = pendingFilesRef.current.get(pendingId)
      setPendingUploads((current) => {
        const pending = current.find((p) => p.id === pendingId)
        if (pending && file) {
          runUpload(pendingId, pending.requirementKey, file)
          return current.map((p) =>
            p.id === pendingId ? { ...p, progress: 0, error: undefined, status: 'uploading' } : p,
          )
        }
        return current
      })
    },
    [runUpload],
  )

  const handleDismiss = useCallback((pendingId: string) => {
    pendingFilesRef.current.delete(pendingId)
    setPendingUploads((current) => current.filter((p) => p.id !== pendingId))
  }, [])

  const handleCancel = useCallback((pendingId: string) => {
    // Abort the in-flight request, then drop the row (the upload promise resolves to a cancel and no-ops).
    pendingControllersRef.current.get(pendingId)?.abort()
    pendingControllersRef.current.delete(pendingId)
    pendingFilesRef.current.delete(pendingId)
    setPendingUploads((current) => current.filter((p) => p.id !== pendingId))
  }, [])

  const removeMutation = useMutation({
    mutationFn: (documentId: string) => deleteDocument(documentId),
    onMutate: (documentId: string) => {
      setRemovingIds((current) => new Set(current).add(documentId))
    },
    onSuccess: () => {
      toast.success('Document removed')
      void queryClient.invalidateQueries({ queryKey: DOCUMENTS_QUERY_KEY })
    },
    onError: (error: unknown) => {
      toast.error(messageFromError(error, 'Could not remove the document. Please try again.'))
    },
    onSettled: (_data, _error, documentId) => {
      setRemovingIds((current) => {
        const next = new Set(current)
        next.delete(documentId)
        return next
      })
    },
  })

  const handleRemoveDocument = useCallback(
    (documentId: string, pendingId?: string) => {
      if (pendingId) {
        pendingFilesRef.current.delete(pendingId)
        setPendingUploads((current) => current.filter((p) => p.id !== pendingId))
      }
      removeMutation.mutate(documentId)
    },
    [removeMutation],
  )

  const pendingByRequirement = useCallback(
    (requirementKey: string) => pendingUploads.filter((p) => p.requirementKey === requirementKey),
    [pendingUploads],
  )

  const requiredRequirements = data?.requirements.filter((r) => !r.optional) ?? []
  const optionalRequirements = data?.requirements.filter((r) => r.optional) ?? []

  // Financial connections (Stripe Financial Connections) are only offered to USA clients today.
  const showFinancialConnections = riskUser?.region === 'usa'

  // Bank verification (Stripe Financial Connections) counts as one more step toward overall
  // completion, but only when it's actually offered for the client's region (USA today).
  const bankConnected = banking?.status === 'connected' && (banking?.accounts.length ?? 0) > 0

  const checklistItems = riskUser
    ? [
        ...requiredRequirements.map((requirement) => ({
          label: requirement.label,
          done: hasSatisfyingDocument(requirement.documents),
          targetId: `verification-${requirement.key}`,
        })),
        ...(showFinancialConnections && banking?.available
          ? [{ label: 'Bank connection', done: bankConnected, targetId: 'verification-bank-connection' }]
          : []),
      ]
    : []

  const expectedChecklistItemCount =
    requiredRequirements.length +
    (showFinancialConnections && banking?.available ? 1 : 0)

  const isBankingReady = !showFinancialConnections || !isBankingLoading
  const isFullyComplete =
    !!riskUser &&
    !isLoading &&
    isBankingReady &&
    checklistItems.length > 0 &&
    checklistItems.every((item) => item.done)

  useEffect(() => {
    if (!riskUser || isLoading || !isBankingReady || checklistItems.length === 0) {
      return
    }

    const previouslyComplete = wasCompleteRef.current

    if (previouslyComplete === null) {
      wasCompleteRef.current = isFullyComplete
      return
    }

    if (!previouslyComplete && isFullyComplete) {
      setShowCompleteDialog(true)
    }

    wasCompleteRef.current = isFullyComplete
  }, [riskUser, isLoading, isBankingReady, isFullyComplete, checklistItems.length])

  if (!riskUser) {
    return <DashboardSkeleton />
  }

  return (
    <div className="mx-auto max-w-6xl px-4 py-8 sm:px-6 lg:px-8 lg:py-12">
      <DashboardWelcomeHero
        displayName={formatPrivateDisplayName(riskUser.firstName, riskUser.lastName)}
        groupName={riskUser.groupName}
        description="Upload the documents below to complete your risk assessment. Everything saves automatically, so you can leave and come back at any time."
      />

      <div className="grid gap-6 lg:grid-cols-3 lg:items-start lg:gap-8">
        <aside className="order-1 space-y-4 lg:order-2 lg:sticky lg:top-24 lg:self-start">
          <DashboardSidebarHeader />
          <ProgressSummaryCard
            items={checklistItems}
            isLoading={isLoading}
            expectedItemCount={
              expectedChecklistItemCount ||
              (riskUser.region === 'usa' ? (banking?.available ? 4 : 3) : 6)
            }
          />
          <SecurityCard />
        </aside>

        <div className="order-2 space-y-8 lg:order-1 lg:col-span-2">
          {isError ? (
            <ErrorState
              title="Couldn't load your documents"
              description="Check your connection and try again, any files you've already uploaded are safe"
              actionLabel="Try again"
              onAction={() => void refetch()}
            />
          ) : isLoading ? (
            <section className="space-y-4">
              <DashboardSectionHeading>Required documents</DashboardSectionHeading>
              <div className="space-y-4">
                <DocumentRequirementCardSkeleton />
                <DocumentRequirementCardSkeleton />
                <DocumentRequirementCardSkeleton />
              </div>
            </section>
          ) : (
            <>
              <section className="space-y-4">
                <DashboardSectionHeading>Required documents</DashboardSectionHeading>
                <div className="space-y-4">
                  {requiredRequirements.map((requirement, index) => (
                    <DocumentRequirementCard
                      key={requirement.key}
                      index={index + 1}
                      requirement={requirement}
                      pendingUploads={pendingByRequirement(requirement.key)}
                      removingIds={removingIds}
                      onFilesSelected={(files) => handleFilesSelected(requirement.key, files)}
                      onRemove={handleRemoveDocument}
                      onRetry={handleRetry}
                      onDismiss={handleDismiss}
                      onCancel={handleCancel}
                      onUploadAnimationComplete={handleUploadAnimationComplete}
                    />
                  ))}
                </div>
              </section>

              {optionalRequirements.length > 0 && (
                <section className="space-y-4">
                  <h2 className="text-sm font-semibold uppercase tracking-wide text-muted-foreground">
                    Optional documents
                  </h2>
                  <div className="space-y-4">
                    {optionalRequirements.map((requirement) => (
                      <DocumentRequirementCard
                        key={requirement.key}
                        requirement={requirement}
                        pendingUploads={pendingByRequirement(requirement.key)}
                        removingIds={removingIds}
                        onFilesSelected={(files) => handleFilesSelected(requirement.key, files)}
                        onRemove={handleRemoveDocument}
                        onRetry={handleRetry}
                        onDismiss={handleDismiss}
                        onCancel={handleCancel}
                        onUploadAnimationComplete={handleUploadAnimationComplete}
                      />
                    ))}
                  </div>
                </section>
              )}

              {showFinancialConnections && (
                <section className="space-y-4">
                  <h2 className="text-sm font-semibold uppercase tracking-wide text-muted-foreground">
                    Financial connections
                  </h2>
                  <BankVerificationCard />
                </section>
              )}
            </>
          )}
        </div>
      </div>

      <VerificationCompleteDialog
        open={showCompleteDialog}
        onOpenChange={setShowCompleteDialog}
      />
    </div>
  )
}
